import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, CalendarClock } from "lucide-react";
import { ARTICLES, getArticle } from "@/lib/resources-content";
import { Disclaimer } from "@/components/site/Disclaimer";
import { NonReservedNotice } from "@/components/site/NonReservedNotice";


export const Route = createFileRoute("/resources/$slug")({
  loader: ({ params }) => {
    const article = getArticle(params.slug);
    if (!article) throw notFound();
    return { article };
  },
  head: ({ loaderData }) => {
    const a = loaderData?.article;
    const title = a
      ? `${a.title} | Resource Centre`
      : "Resource | Nkadimeng Danny Legal";
    const desc = a?.summary ?? "Legal insights and guides for South Africa.";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center">
      <h1 className="font-display text-3xl text-navy">Article not found</h1>
      <Link to="/resources" className="mt-4 inline-block text-navy underline">
        Back to resources
      </Link>
    </div>
  ),
  component: ArticlePage,
});

function ArticlePage() {
  const { article } = Route.useLoaderData();
  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-14 md:py-20">
          <Link
            to="/resources"
            className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-navy"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All resources
          </Link>
          <div className="mt-5 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-navy/10 px-2.5 py-0.5 font-medium text-navy">
              {article.tag}
            </span>
            <span className="inline-flex items-center gap-1 text-muted-foreground">
              <CalendarClock className="h-3.5 w-3.5" />
              {article.eta}
            </span>
          </div>
          <span className="gold-rule mt-4" />
          <h1 className="font-display text-3xl text-navy md:text-5xl">
            {article.title}
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            {article.summary}
          </p>
        </div>
      </section>

      <section className="container-narrow grid gap-12 py-12 md:grid-cols-[1.4fr_1fr]">
        <article className="prose-article space-y-8">
          {article.body.map((block: { heading?: string; paragraphs: string[]; list?: string[] }, idx: number) => (
            <div key={idx}>
              {block.heading && (
                <h2 className="font-display text-2xl text-navy">
                  {block.heading}
                </h2>
              )}
              {block.paragraphs.map((p: string, i: number) => (
                <p
                  key={i}
                  className="mt-3 leading-relaxed text-foreground/85"
                >
                  {p}
                </p>
              ))}
              {block.list && (
                <ul className="mt-4 space-y-2">
                  {block.list.map((li: string) => (
                    <li
                      key={li}
                      className="flex items-start gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm"
                    >
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                      <span>{li}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
          <NonReservedNotice className="mb-3" />
          <Disclaimer variant="compact" />

        </article>

        <aside className="h-fit space-y-5 md:sticky md:top-28">
          <div className="rounded-xl bg-navy p-8 text-navy-foreground shadow-lifted">
            <h3 className="font-display text-xl">Need help on this?</h3>
            <p className="mt-3 text-sm text-navy-foreground/80">
              Book a one-on-one consultation and we'll apply this directly to
              your matter.
            </p>
            <Link
              to="/book"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-gold px-5 py-2.5 text-sm font-semibold text-gold-foreground"
            >
              Book consultation <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">
              Want tailored notes?
            </p>
            <p className="mt-3 text-sm text-muted-foreground">
              Order custom study or briefing notes on this topic — no
              consultation required.
            </p>
            <Link
              to={"/study-notes" as any}
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-navy hover:text-gold"
            >
              Order tailored notes <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </aside>
      </section>

      <section className="bg-secondary/40 py-14">
        <div className="container-narrow">
          <h2 className="font-display text-2xl text-navy">More resources</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {related.map((r) => (
              <Link
                key={r.slug}
                to="/resources/$slug"
                params={{ slug: r.slug }}
                className="block rounded-xl border border-border bg-card p-5 shadow-soft hover:-translate-y-0.5 hover:shadow-lifted transition"
              >
                <span className="rounded-full bg-navy/10 px-2 py-0.5 text-[10px] font-medium text-navy">
                  {r.tag}
                </span>
                <h3 className="mt-3 font-display text-base text-navy">
                  {r.title}
                </h3>
                <p className="mt-2 text-xs text-muted-foreground">
                  {r.eta}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
